import React, { useCallback, useEffect, useState } from 'react';
import { Plug, Radio, Webhook } from 'lucide-react';
import {
  listEdgeConnectorResources,
  updateEdgeConnectorResources,
} from '../../../api/edgeConnectors';
import { getApiErrorMessage } from '../../../utils/errors';

function formatCatalogDate(value) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
}

export function McpCatalogStatus({ resource }) {
  const catalogDate = formatCatalogDate(resource.edge_catalog_accepted_at);

  if (resource.edge_catalog_status === 'missing') {
    return (
      <span className="block text-xs text-red-700 mt-0.5">
        Catálogo ainda não publicado pelo connector. As ferramentas deste MCP não
        estão disponíveis.
      </span>
    );
  }

  if (resource.edge_catalog_status === 'available') {
    return (
      <span className="block text-xs text-emerald-700 mt-0.5">
        Catálogo publicado · {resource.edge_catalog_tool_count || 0} ferramenta
        {resource.edge_catalog_tool_count === 1 ? '' : 's'}
        {catalogDate ? ` · atualizado em ${catalogDate}` : ''}
      </span>
    );
  }

  return null;
}

/**
 * Atribuição de canais e MCPs a um connector.
 *
 * A referência é o único identificador que atravessa a fronteira: o Edge a usa
 * para achar o endereço e a credencial no mapa local dele. Por isso ela é
 * editável aqui e precisa bater exatamente com o `config/resources.yaml` do
 * cliente — a tela diz isso, porque errar a referência é o motivo número um de um
 * recurso voltar como `local_resource_not_found`.
 */
function ResourceRow({ resource, kind, connectorId, onToggle, onRefChange, disabled }) {
  // Comparar com o connector desta tela, e não com "tem algum dono": desmarcar um
  // recurso deste connector deixava `assigned_connector_id` apontando para ele
  // até salvar, o que travava o checkbox e impedia desfazer a ação.
  const takenByAnother =
    Boolean(resource.assigned_connector_id) &&
    resource.assigned_connector_id !== connectorId;
  const Icon = kind === 'channel' ? Radio : Plug;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2 border-b border-slate-100 last:border-0">
      <label className="flex items-start gap-2 flex-1 min-w-0 cursor-pointer">
        <input
          type="checkbox"
          checked={resource.assigned}
          onChange={(event) => onToggle(resource, event.target.checked)}
          disabled={disabled || takenByAnother}
          className="mt-1 h-4 w-4 shrink-0"
        />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm text-content-primary">
            <Icon className="h-3.5 w-3.5 shrink-0 text-content-subtle" aria-hidden />
            {resource.name}
          </span>
          <span className="block text-xs text-content-muted mt-0.5">
            {kind === 'channel'
              ? [
                  // O agente é o que diferencia dois canais de mesmo nome, e nome
                  // de canal costuma ser genérico ("WhatsApp").
                  resource.agent_name && `agente ${resource.agent_name}`,
                  resource.type,
                  resource.provider,
                  resource.phone_number,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : resource.transport}
            {!resource.enabled && ' · desativado'}
            {takenByAnother &&
              ` · já atribuído a ${resource.assigned_connector_name || 'outro connector'}`}
          </span>
          {kind === 'channel' && resource.catalog_configured === false && (
            <span className="block text-xs text-amber-700 mt-0.5">
              Sem slot de catálogo: o Edge recebe este canal com provider
              desconhecido até o slot ser configurado.
            </span>
          )}
          {kind === 'mcp' && <McpCatalogStatus resource={resource} />}
        </span>
      </label>

      {resource.assigned && (
        <input
          type="text"
          value={resource.resource_ref || ''}
          onChange={(event) => onRefChange(resource, event.target.value)}
          placeholder={resource.suggested_resource_ref}
          disabled={disabled}
          spellCheck={false}
          className="field-input font-mono text-xs sm:w-72 shrink-0"
          aria-label={`Referência de ${resource.name}`}
        />
      )}
    </div>
  );
}

/**
 * Endpoint HTTP não é atribuível: a referência é escolhida no webhook do agente e
 * quem executa o job é o connector do canal daquela conversa. A linha existe para
 * conferência com o `config/resources.yaml` — e para avisar quando nenhum
 * connector receberia a referência, caso em que o webhook não sai da fila.
 */
export function HttpEndpointRow({ endpoint, connectorName }) {
  const executedBy = endpoint.assigned
    ? connectorName
    : endpoint.connector_names.join(', ');

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2 border-b border-slate-100 last:border-0">
      <span className="flex-1 min-w-0 sm:pl-6">
        <span className="flex items-center gap-1.5 text-sm text-content-primary">
          <Webhook className="h-3.5 w-3.5 shrink-0 text-content-subtle" aria-hidden />
          {endpoint.agent_name}
        </span>
        <span className="block text-xs text-content-muted mt-0.5">
          {[
            `webhook de ${endpoint.sub_agent_name}`,
            !endpoint.enabled && 'desativado',
            executedBy && `executado por ${executedBy}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {!executedBy && (
          <span className="block text-xs text-amber-700 mt-0.5">
            Nenhum canal deste agente está atribuído a um connector: o webhook fica
            na fila sem ninguém para executar.
          </span>
        )}
      </span>

      <span className="field-input font-mono text-xs sm:w-72 shrink-0 bg-slate-50 text-content-muted truncate">
        {endpoint.resource_ref}
      </span>
    </div>
  );
}

function ResourceAssignmentPanel({ connector, canManage, onSaved }) {
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setResources(await listEdgeConnectorResources(connector.id));
      setDirty(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os recursos.'));
    } finally {
      setLoading(false);
    }
  }, [connector.id]);

  useEffect(() => {
    load();
  }, [load]);

  const updateList = (kind, resource, changes) => {
    setResources((current) => ({
      ...current,
      [kind]: current[kind].map((item) =>
        item.id === resource.id ? { ...item, ...changes } : item,
      ),
    }));
    setDirty(true);
    setSaved('');
  };

  const handleToggle = (kind) => (resource, assigned) =>
    updateList(kind, resource, {
      assigned,
      resource_ref: assigned
        ? resource.resource_ref || resource.suggested_resource_ref
        : null,
    });

  const handleRefChange = (kind) => (resource, value) =>
    updateList(kind, resource, { resource_ref: value });

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved('');
    try {
      const selected = (items) =>
        items
          .filter((item) => item.assigned)
          .map((item) => ({ id: item.id, resource_ref: item.resource_ref }));

      const result = await updateEdgeConnectorResources(connector.id, {
        channels: selected(resources.channels),
        mcp_servers: selected(resources.mcp_servers),
      });
      setResources({
        channels: result.channels,
        mcp_servers: result.mcp_servers,
        http_endpoints: result.http_endpoints || [],
      });
      setDirty(false);
      const version = result.configuration?.desired_configuration_version;
      setSaved(
        version
          ? `Recursos salvos. Configuração v${version} publicada; o connector aplica no próximo ciclo.`
          : 'Recursos salvos.',
      );
      if (onSaved) {
        onSaved();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar os recursos.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-content-muted">Carregando recursos...</p>;
  }

  if (!resources) {
    return error ? <p className="text-sm text-red-700">{error}</p> : null;
  }

  const httpEndpoints = resources.http_endpoints || [];
  const isEmpty =
    resources.channels.length === 0 &&
    resources.mcp_servers.length === 0 &&
    httpEndpoints.length === 0;

  return (
    <div className="space-y-4 pt-2 border-t border-slate-100">
      <div>
        <p className="text-sm font-medium text-content-primary">
          Recursos deste connector
        </p>
        <p className="text-xs text-content-muted mt-1">
          A referência (ex.: <code className="font-mono">mcp:erp-prod</code>) precisa
          existir no <code className="font-mono">config/resources.yaml</code> do
          cliente. É por ela que o Edge encontra endereço e credencial — que nunca
          saem da rede dele. Referência sem par no arquivo volta como{' '}
          <span className="font-mono">local_resource_not_found</span>.
        </p>
      </div>

      {error && <p className="text-sm text-red-700">{error}</p>}
      {saved && !dirty && <p className="text-sm text-emerald-700">{saved}</p>}

      {isEmpty ? (
        <p className="text-sm text-content-muted">
          Esta conta ainda não tem canais nem servidores MCP para atribuir.
        </p>
      ) : (
        <>
          {resources.channels.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-content-subtle mb-1">
                Canais
              </p>
              {resources.channels.map((resource) => (
                <ResourceRow
                  key={resource.id}
                  resource={resource}
                  kind="channel"
                  connectorId={connector.id}
                  onToggle={handleToggle('channels')}
                  onRefChange={handleRefChange('channels')}
                  disabled={!canManage || saving}
                />
              ))}
            </div>
          )}

          {resources.mcp_servers.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-content-subtle mb-1">
                Servidores MCP
              </p>
              {resources.mcp_servers.map((resource) => (
                <ResourceRow
                  key={resource.id}
                  resource={resource}
                  kind="mcp"
                  connectorId={connector.id}
                  onToggle={handleToggle('mcp_servers')}
                  onRefChange={handleRefChange('mcp_servers')}
                  disabled={!canManage || saving}
                />
              ))}
            </div>
          )}

          {httpEndpoints.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-content-subtle mb-1">
                Endpoints HTTP
              </p>
              <p className="text-xs text-content-muted mb-1">
                Referências que os webhooks dos agentes usam. Não se atribuem aqui:
                cada uma é definida no webhook do agente e executada pelo connector
                do canal daquela conversa. Precisa existir em{' '}
                <code className="font-mono">http_endpoints</code> no{' '}
                <code className="font-mono">config/resources.yaml</code> desse
                connector.
              </p>
              {httpEndpoints.map((endpoint) => (
                <HttpEndpointRow
                  key={endpoint.id}
                  endpoint={endpoint}
                  connectorName={connector.name}
                />
              ))}
            </div>
          )}

          {canManage && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !dirty}
                className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar recursos'}
              </button>
              {dirty && (
                <span className="text-xs text-content-muted">
                  Salvar publica uma versão nova de configuração.
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ResourceAssignmentPanel;
